/**
 * Dispute form dialog, shared by the admin transaction drawer and the employee
 * transaction actions menu. Opening in a dialog keeps the drawer and the list
 * behind it intact, and avoids threading the transaction through router state.
 *
 * Main rendered this with plain MUI controls on purpose, because two themes were
 * live there and a bare field picked up chrome from both. On this branch the
 * legacy admin theme and dsTheme are both deleted and the package is the only
 * theme, so that reason is gone and the DS fields are back. Their own `label` /
 * `required` / `placeholder` also replace the hand-rolled label helper and the
 * `renderValue` placeholder this file used to carry.
 */
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { useAuth } from "@frontegg/react";
import { useFormik } from "formik";
import { isAxiosError } from "axios";
import {
  FormControlLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Checkbox, IconButton, Select, TextField, toast } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { DisputeReason } from "enums/DisputeReason";
import { DisputeProductType } from "enums/DisputeProductType";
import { UPLOAD_FILE_ACCEPT } from "utils/files/uploadFileTypes";
import disputeValidationSchema from "validations/dispute";
import { formatCashFull } from "utils/formatCash";
import httpClient from "utils/httpClient";

/** Only the fields the form actually reads, so each caller can pass its own shape. */
export type DisputableTransaction = {
  id: string;
  amount?: string | number | null;
  transactionDate?: string | number | null;
  merchantName?: string | null;
};

type DisputeTransactionDialogProps = {
  open: boolean;
  transaction: DisputableTransaction | undefined;
  onClose: () => void;
  /** Called after a successful submit, e.g. to refetch the transaction. */
  onCompleted?: () => void;
};

interface DisputeFormValues {
  reason: DisputeReason | "";
  productType: DisputeProductType | "";
  description: string;
}

const MAX_DISPUTE_ATTACHMENTS = 5;

const DisputeTransactionDialog: React.FC<DisputeTransactionDialogProps> = ({
  open,
  transaction,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [evidence, setEvidence] = useState<Record<string, string>>({});
  const attempt = useRef<{
    key: string;
    consentAt: string;
    disputeId?: string;
  } | null>(null);

  // The package Select keys options by `id`, and the enum values are the wire
  // format, so the two line up without a lookup table.
  const reasonOptions = [
    { id: DisputeReason.FRAUDULENT, label: t("Fraudulent") },
    { id: DisputeReason.NOT_RECEIVED, label: t("Not received") },
    { id: DisputeReason.DUPLICATE_CHARGE, label: t("Duplicate charge") },
    { id: DisputeReason.NOT_AS_DESCRIBED, label: t("Not as described") },
    { id: DisputeReason.PREVIOUSLY_CANCELED, label: t("Previously canceled") },
    { id: DisputeReason.OTHER, label: t("Other") },
  ];

  const productTypeOptions = [
    { id: DisputeProductType.SERVICES, label: t("Services") },
    { id: DisputeProductType.MERCHANDISE, label: t("Merchandise") },
  ];

  const formik = useFormik<DisputeFormValues>({
    initialValues: {
      reason: "",
      productType: "",
      description: "",
    },
    validationSchema: disputeValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!transaction?.id || !consentAccepted) return;
      const fields = evidenceFields(values.reason, values.productType);
      if (fields.some((field) => !evidence[field])) {
        toast.error(t("Complete the additional evidence fields"));
        return;
      }
      attempt.current ??= {
        key: crypto.randomUUID(),
        consentAt: new Date().toISOString(),
      };
      setSubmitting(true);
      try {
        const formData = new FormData();
        formData.append("reason", values.reason);
        formData.append("productType", values.productType);
        formData.append("description", values.description.trim());
        const details = Object.fromEntries(
          fields.map((field) => [
            field,
            field.endsWith("_at")
              ? Math.floor(Date.parse(`${evidence[field]}T00:00:00Z`) / 1000)
              : field === "cancellation_policy_provided"
                ? evidence[field] === "true"
                : evidence[field],
          ]),
        );
        formData.append("evidence", JSON.stringify(details));
        formData.append("consentAccepted", "true");
        formData.append("consentAcceptedAt", attempt.current.consentAt);
        if (attempt.current.disputeId)
          formData.append("disputeId", attempt.current.disputeId);
        attachments.forEach((file) => formData.append("attachments", file));

        const response = await httpClient.post<{
          kind: "FILED" | "SUPPORT_REQUESTED";
        }>(`/api/v1/transactions/${transaction.id}/dispute`, formData, {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
            "Idempotency-Key": attempt.current.key,
          },
        });

        toast.success(
          response.data.kind === "FILED"
            ? t("Your dispute has been filed with the issuer")
            : t(
                "Support received your request A dispute has not been filed yet",
              ),
        );
        formik.resetForm();
        setAttachments([]);
        setEvidence({});
        setConsentAccepted(false);
        attempt.current = null;
        onClose();
        onCompleted?.();
      } catch (err) {
        const problem = isAxiosError<{ code?: string; disputeId?: string }>(err)
          ? err.response?.data
          : undefined;
        const code = problem?.code;
        if (
          code === "PROVIDER_REJECTED" &&
          problem?.disputeId &&
          attempt.current
        ) {
          attempt.current = {
            ...attempt.current,
            key: crypto.randomUUID(),
            disputeId: problem.disputeId,
          };
        }
        toast.error(
          code === "OPERATION_OUTCOME_UNKNOWN"
            ? t(
                "The issuer is still confirming your dispute Retry to check the same request",
              )
            : code === "PROVIDER_REJECTED"
              ? t(
                  "The issuer needs changes to your evidence Review it and retry",
                )
              : t("Error submitting dispute Please try again"),
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    if (submitting) return;
    formik.resetForm();
    setAttachments([]);
    setEvidence({});
    setConsentAccepted(false);
    attempt.current = null;
    onClose();
  };

  const handleAddFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const combined = [...attachments, ...Array.from(fileList)];
    if (combined.length > MAX_DISPUTE_ATTACHMENTS) {
      toast.error(
        t("You can attach up to {{count}} files", {
          count: MAX_DISPUTE_ATTACHMENTS,
        }),
      );
    }
    setAttachments(combined.slice(0, MAX_DISPUTE_ATTACHMENTS));
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((files) => files.filter((_, i) => i !== index));
  };

  const handleOpenFile = (file: File) => {
    const url = URL.createObjectURL(file);
    window.open(url, "_blank", "noopener,noreferrer");
    // Revoke after the new tab has had time to load the blob.
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const fieldError = (field: keyof DisputeFormValues) =>
    formik.touched[field] && formik.errors[field]
      ? t(formik.errors[field] as string)
      : undefined;

  const evidenceLabels: Record<string, string> = {
    expected_at: t("Expected delivery date"),
    received_at: t("Date received"),
    returned_at: t("Return attempt date"),
    canceled_at: t("Cancellation date"),
    return_description: t("Describe your attempt to return the purchase"),
    return_status: t("Return outcome"),
    cancellation_reason: t("Reason for cancellation"),
    cancellation_policy_provided: t(
      "Did the merchant provide a cancellation policy",
    ),
  };

  const amountDisplay =
    transaction?.amount != null && !isNaN(Number(transaction.amount))
      ? formatCashFull(Math.abs(Number(transaction.amount)))
      : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ padding: "var(--space-7) var(--space-8)" }}>
        <Inline justify="between" wrap={false} gap="var(--space-2)">
          <Stack gap="var(--space-1)">
            <Typography variant="h6">{t("Transaction dispute")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("Provide information on the transaction you wish to dispute")}
            </Typography>
          </Stack>
          <IconButton
            variant="ghost"
            size="small"
            aria-label={t("Close") as string}
            onClick={handleClose}
            disabled={submitting}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={18} />
          </IconButton>
        </Inline>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: "var(--space-7) var(--space-8)" }}>
        <form id="dispute-form" onSubmit={formik.handleSubmit}>
          <Stack>
            <Stack gap="var(--space-1)">
              <Typography variant="subtitle2">
                {transaction?.merchantName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {amountDisplay}
                {transaction?.transactionDate
                  ? ` · ${dayjs(transaction.transactionDate).format("MMM D, YYYY")}`
                  : ""}
              </Typography>
            </Stack>

            <Select
              label={t("Reason for Dispute")}
              required
              placeholder={t("Select an option")}
              options={reasonOptions}
              value={formik.values.reason}
              // setFieldValue, not a synthetic event: formik's handleChange
              // resolves the field off event.target and silently no-ops.
              onChange={(value) => {
                formik.setFieldValue("reason", value ?? "");
                formik.setFieldTouched("reason", true);
              }}
              errorText={fieldError("reason")}
            />

            <Select
              label={t("Product Type Purchased")}
              required
              placeholder={t("Select an option")}
              options={productTypeOptions}
              value={formik.values.productType}
              // setFieldValue, not a synthetic event: formik's handleChange
              // resolves the field off event.target and silently no-ops.
              onChange={(value) => {
                formik.setFieldValue("productType", value ?? "");
                formik.setFieldTouched("productType", true);
              }}
              errorText={fieldError("productType")}
            />

            <TextField
              label={t("Description")}
              required
              name="description"
              fullWidth
              multiline
              minRows={5}
              maxRows={10}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorText={fieldError("description")}
              placeholder={t("Describe the reason for dispute") as string}
            />

            {evidenceFields(
              formik.values.reason,
              formik.values.productType,
            ).map((field) =>
              field === "return_status" ||
              field === "cancellation_policy_provided" ? (
                <Select
                  key={field}
                  label={evidenceLabels[field]}
                  required
                  value={evidence[field] || ""}
                  options={
                    field === "return_status"
                      ? [
                          { id: "successful", label: t("Successful") },
                          {
                            id: "merchant_rejected",
                            label: t("Merchant rejected return"),
                          },
                        ]
                      : [
                          { id: "true", label: t("Yes") },
                          { id: "false", label: t("No") },
                        ]
                  }
                  onChange={(value) =>
                    setEvidence((current) => ({
                      ...current,
                      [field]: String(value || ""),
                    }))
                  }
                />
              ) : (
                <TextField
                  key={field}
                  label={evidenceLabels[field]}
                  required
                  type={field.endsWith("_at") ? "date" : "text"}
                  value={evidence[field] || ""}
                  onChange={(event) =>
                    setEvidence((current) => ({
                      ...current,
                      [field]: event.target.value,
                    }))
                  }
                />
              ),
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentAccepted}
                  onChange={(event) => setConsentAccepted(event.target.checked)}
                />
              }
              label={t(
                "I confirm the cardholder consents to filing this dispute and the information is accurate",
              )}
            />
            <Typography variant="caption" color="text.secondary">
              {t(
                "Eligible disputes are filed with the issuer Other requests go to support and are not yet filed",
              )}
            </Typography>

            <Stack gap="var(--space-3)">
              <Stack gap="var(--space-1)">
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-secondary)" }}
                >
                  {t("Attachments")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("Attach files to support your dispute as evidence")}
                </Typography>
              </Stack>

              {attachments.length > 0 && (
                <Stack gap="var(--space-1)">
                  {attachments.map((file, index) => (
                    <Inline
                      key={`${file.name}-${index}`}
                      justify="between"
                      wrap={false}
                      gap="var(--space-2)"
                      onClick={() => handleOpenFile(file)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {file.name}
                      </Typography>
                      <IconButton
                        variant="ghost"
                        size="small"
                        aria-label={t("Remove attachment") as string}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                      >
                        <HugeiconsIcon icon={Cancel01Icon} size={18} />
                      </IconButton>
                    </Inline>
                  ))}
                </Stack>
              )}

              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                accept={UPLOAD_FILE_ACCEPT}
                onClick={(e) => {
                  // Allow re-selecting the same file after removing it.
                  (e.target as HTMLInputElement).value = "";
                }}
                onChange={(e) => handleAddFiles(e.target.files)}
              />
              <Button
                variant="default-outlined"
                sx={{ width: "fit-content" }}
                disabled={attachments.length >= MAX_DISPUTE_ATTACHMENTS}
                onClick={() => fileInputRef.current?.click()}
              >
                {t("Add attachment")}
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>

      <DialogActions
        sx={{ padding: "var(--space-6) var(--space-8)", gap: "var(--space-3)" }}
      >
        <Button
          variant="primary-outlined"
          onClick={handleClose}
          disabled={submitting}
        >
          {t("Cancel")}
        </Button>
        <Button
          type="submit"
          form="dispute-form"
          variant="primary-filled"
          disabled={submitting || !consentAccepted}
        >
          {submitting ? t("Submitting") : t("Submit dispute")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisputeTransactionDialog;

function evidenceFields(
  reason: DisputeReason | "",
  productType: DisputeProductType | "",
): string[] {
  if (reason === DisputeReason.NOT_RECEIVED) return ["expected_at"];
  if (reason === DisputeReason.PREVIOUSLY_CANCELED)
    return ["canceled_at", "cancellation_policy_provided"];
  if (reason === DisputeReason.NOT_AS_DESCRIBED) {
    return productType === DisputeProductType.SERVICES
      ? ["received_at", "canceled_at", "cancellation_reason"]
      : ["received_at", "returned_at", "return_description", "return_status"];
  }
  return [];
}
